import {
  Appointment,
  AppointmentCreateData,
  AppointmentDTO,
  AppointmentFeedLog,
  AppointmentStatusCode,
  AppointmentStatusHistory,
  PaginatedResponse,
  SkipValidationCode,
  UpdateAppointmentObservationsRequest,
} from "../../types";
import { PENDING_APPOINTMENT_STATUSES } from "@/utils/appointmentStatuses";
import { apiUrl } from "../../utils/url";
import {
  Appointment as AgendaAppointment,
  WaitingRoomAppointmentsResponse,
} from "../agenda/types";
import { getErrorMessage } from "../errors";
import { HttpClient, HttpError } from "../HttpClient";
import {
  AttendeeAppointmentSearchParams,
  IAppointmentsService,
  PendingAppointmentsParams,
} from "./IAppointmentsService";

const shouldPreserveAppointmentHttpError = (error: unknown): boolean =>
  error instanceof HttpError &&
  error.responseData &&
  typeof error.responseData === "object" &&
  "errorCode" in error.responseData &&
  [
    "ATTENDEE_ALREADY_HAS_APPOINTMENT_THIS_WEEK",
    "APPOINTMENT_OVERLAPPING",
  ].includes(String(error.responseData.errorCode));

export class AppointmentsService implements IAppointmentsService {
  private httpClient: HttpClient;

  constructor() {
    this.httpClient = new HttpClient({
      baseURL: apiUrl,
      headers: {},
    });
  }

  async getAppointmentById(id: number): Promise<Appointment> {
    try {
      return await this.httpClient.get<Appointment>(`/appointments/${id}`);
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar o agendamento. Atualize a página e confirme se o agendamento ainda existe.",
      );
      throw new Error(message);
    }
  }

  async createAppointment(
    data: AppointmentCreateData,
    skipValidationCodes?: SkipValidationCode[],
  ): Promise<number> {
    const queryParams = new URLSearchParams();
    if (skipValidationCodes && skipValidationCodes.length > 0) {
      skipValidationCodes.forEach(code => {
        queryParams.append("skipValidationCodes", code);
      });
    }

    const endpoint =
      queryParams.toString() !== ""
        ? `/appointments?${queryParams.toString()}`
        : `/appointments`;

    try {
      return await this.httpClient.post<number>(endpoint, data, {}, true);
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível criar o agendamento. Confirme o animal, serviço, funcionário e horário selecionados.",
      );

      // Preserve validation codes that the UI can resolve with confirmation/retry flows.
      if (shouldPreserveAppointmentHttpError(error)) {
        throw error;
      }
      throw new Error(message);
    }
  }

  async updateAppointment(
    id: number,
    data: AppointmentDTO,
    skipValidationCodes: SkipValidationCode[] = [
      "APPOINTMENT_VAL_ATTENDEE_SCHEDULE",
    ],
  ): Promise<Appointment> {
    const queryParams = new URLSearchParams();
    if (skipValidationCodes && skipValidationCodes.length > 0) {
      skipValidationCodes.forEach(code => {
        queryParams.append("skipValidationCodes", code);
      });
    }

    const endpoint =
      queryParams.toString() !== ""
        ? `/appointments/${id}?${queryParams.toString()}`
        : `/appointments/${id}`;

    try {
      return await this.httpClient.put<Appointment>(endpoint, data);
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível atualizar o agendamento. Atualize a página e confirme se o horário ainda está disponível.",
      );
      if (shouldPreserveAppointmentHttpError(error)) {
        throw error;
      }
      throw new Error(message);
    }
  }

  async updateAppointmentServiceAllocation(
    id: number,
    data: Pick<AppointmentDTO, "employeeSlotId" | "serviceId">,
    skipValidationCodes?: SkipValidationCode[],
  ): Promise<void> {
    const queryParams = new URLSearchParams();
    queryParams.append("serviceId", String(data.serviceId));
    queryParams.append("employeeSlotId", String(data.employeeSlotId));

    if (skipValidationCodes && skipValidationCodes.length > 0) {
      skipValidationCodes.forEach(code => {
        queryParams.append("skipValidation", code);
      });
    }

    try {
      await this.httpClient.patch<void>(
        `/appointments/${id}/update-service-allocation?${queryParams.toString()}`,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível alterar o serviço ou funcionário do agendamento. Escolha um serviço compatível com o horário selecionado.",
      );
      throw new Error(message);
    }
  }

  async updateAppointmentObservations(
    id: number,
    data: UpdateAppointmentObservationsRequest,
  ): Promise<void> {
    try {
      await this.httpClient.patch<void>(
        `/appointments/${id}/update-observations`,
        data,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível guardar as observações do agendamento. Tente guardar novamente.",
      );
      throw new Error(message);
    }
  }

  async updateAppointmentStatus(
    id: number,
    statusCode: AppointmentStatusCode,
  ): Promise<void> {
    try {
      await this.httpClient.patch<void>(
        `/appointments/${id}/update-status?newStatusCode=${statusCode}`,
        {},
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível alterar o estado do agendamento. Atualize a página e confirme o estado atual antes de tentar novamente.",
      );
      throw new Error(message);
    }
  }

  async getAppointmentFeedLogs(params: {
    appointmentId: number;
  }): Promise<AppointmentFeedLog[]> {
    try {
      const { appointmentId } = params;

      const queryParams = new URLSearchParams();
      queryParams.append("appointmentId", appointmentId.toString());

      return await this.httpClient.get<AppointmentFeedLog[]>(
        `/appointments/feed?${queryParams.toString()}`,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar o histórico do agendamento. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async searchAttendeeAppointments(
    attendeeId: number,
    params: AttendeeAppointmentSearchParams,
  ): Promise<PaginatedResponse<Appointment>> {
    return this.postAttendeeAppointmentSearch(
      attendeeId,
      params,
      "Não foi possível carregar os agendamentos do animal. Atualize a página e tente novamente.",
    );
  }

  async getPendingAppointmentsByAttendeeId(
    id: number,
    params: PendingAppointmentsParams,
  ): Promise<PaginatedResponse<Appointment>> {
    return this.postAttendeeAppointmentSearch(
      id,
      {
        page: params.page - 1, // backend is 0-based
        size: params.size,
        sort: params.sort,
        ...(params.serviceIdList?.length
          ? { serviceIdList: params.serviceIdList }
          : {}),
        statusCodeList: PENDING_APPOINTMENT_STATUSES,
      },
      "Não foi possível carregar a lista de agendamentos pendentes. Atualize a página e tente novamente.",
    );
  }

  private async postAttendeeAppointmentSearch(
    attendeeId: number,
    { page, size, sort, ...filters }: AttendeeAppointmentSearchParams,
    fallbackMessage: string,
  ): Promise<PaginatedResponse<Appointment>> {
    const queryParams = new URLSearchParams();
    if (page !== undefined) queryParams.append("page", String(page));
    if (size !== undefined) queryParams.append("size", String(size));
    if (sort) queryParams.append("sort", sort);
    const query = queryParams.toString();

    try {
      return await this.httpClient.post<PaginatedResponse<Appointment>>(
        `/appointments/attendee/${attendeeId}/search${query ? `?${query}` : ""}`,
        filters,
      );
    } catch (error) {
      throw new Error(getErrorMessage(error, fallbackMessage));
    }
  }

  async getAppointmentStatusHistoryById(
    id: number,
  ): Promise<AppointmentStatusHistory[]> {
    try {
      return await this.httpClient.get<AppointmentStatusHistory[]>(
        `/appointments/${id}/status-history`,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar o histórico do agendamento. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async getWaitingRoomAppointments(
    storeId: number,
    page: number = 0,
    size: number = 50,
  ): Promise<WaitingRoomAppointmentsResponse> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append("size", size.toString());
      queryParams.append("page", page.toString());

      return await this.httpClient.get<WaitingRoomAppointmentsResponse>(
        `/appointments/store/${storeId}/waiting-room?${queryParams.toString()}`,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar os agendamentos da sala de espera. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }

  async getAppointmentsByCalendarParameters(params: {
    storeId: number;
    start: string;
    end: string;
    serviceTypeId: number | null;
    employeeId: number | null;
  }): Promise<AgendaAppointment[]> {
    try {
      return await this.httpClient.post<AgendaAppointment[]>(
        "/appointments/calendar",
        params,
      );
    } catch (error) {
      const message = getErrorMessage(
        error,
        "Não foi possível carregar os agendamentos do calendário. Atualize a página e tente novamente.",
      );
      throw new Error(message);
    }
  }
}
